<template>
  <MainLayout
    :initialDrawerExpanded="false"
    contentClass="pa-0"
    :contentStyle="{ overflow: 'hidden' }"
    :has-sidebar="false"
  >
    <template #header>
      <div class="overline">
        <PageBack />
      </div>
    </template>

    <template #content>
      <div class="manuel-root">
      <SupportHero v-if="hero" compact title="Manuel utilisateur" icon="mdi-book-open-outline" />
      <div class="page-layout">
        <!-- TOC Sidebar -->
        <div v-if="toc.length" class="toc-pane d-none d-lg-block">
          <div class="pa-4 mb-8">
            <p class="overline mb-2 grey--text text--darken-1">Table des matières</p>
            <nav>
              <a
                v-for="item in toc"
                :key="item.id"
                :href="`#${item.id}`"
                class="toc-link"
                :class="[`toc-level-${item.level}`, { 'toc-active': activeId === item.id }]"
                @click.prevent="scrollTo(item.id)"
              >{{ item.text }}</a>
            </nav>
          </div>
        </div>

        <!-- Main Content -->
        <div class="content-pane" ref="contentPane">
          <v-row justify="start" class="pa-2 ma-0">
            <v-col cols="12" md="10" xl="8">
              <div v-if="loading" class="text-center pa-8">
                <v-progress-circular indeterminate color="primary" />
              </div>
              <div v-else-if="error" class="text-center pa-8">
                <v-icon large color="error">mdi-alert-circle-outline</v-icon>
                <p class="mt-2">Impossible de charger le manuel.</p>
              </div>
              <div v-else class="manuel-content" v-html="renderedContent" />
            </v-col>
          </v-row>
        </div>
      </div>
      </div>
    </template>
  </MainLayout>
</template>

<script>
import PageBack from '@/components/ui/PageBack.vue'
import MainLayout from '@/layout/MainLayout.vue'
import SupportHero from '@/components/support/SupportHero.vue'
import MarkdownIt from 'markdown-it'

// html: true is safe here — this markdown is generated by our own build script
// (npm run docs / npm run legal-style pipeline), not third-party/user content.
const md = new MarkdownIt({ html: true, linkify: true })

const defaultHeadingOpen = md.renderer.rules.heading_open || function (tokens, idx, options, env, self) {
  return self.renderToken(tokens, idx, options)
}
md.renderer.rules.heading_open = function (tokens, idx, options, env, self) {
  const inlineToken = tokens[idx + 1]
  const text = inlineToken.children.map(t => t.content).join('')
  tokens[idx].attrSet('id', slugify(text))
  return defaultHeadingOpen(tokens, idx, options, env, self)
}

function slugify(text) {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
}

export default {
  name: 'ManuelContent',
  components: { PageBack, SupportHero, MainLayout },
  props: {
    src: {
      type: String,
      required: true,
    },
    /** Show the support hero banner (user manual only). */
    hero: { type: Boolean, default: false },
  },
  data: () => ({
    renderedContent: '',
    toc: [],
    loading: true,
    error: false,
    activeId: null,
  }),
  async created() {
    this.$tocObserver = null
    try {
      const res = await fetch(this.src)
      if (!res.ok) throw new Error('fetch failed')
      const text = await res.text()
      this.toc = this.buildToc(text)
      this.renderedContent = md.render(text)
    } catch {
      this.error = true
    } finally {
      this.loading = false
    }
  },
  mounted() {
    this.$nextTick(() => this.setupObserver())
  },
  updated() {
    this.$nextTick(() => this.setupObserver())
  },
  beforeDestroy() {
    if (this.$tocObserver) this.$tocObserver.disconnect()
  },
  methods: {
    buildToc(text) {
      return text
        .split('\n')
        .map(line => {
          const m = line.match(/^(#{1,3})\s+(.+)/)
          if (!m) return null
          return { level: m[1].length, text: m[2].trim(), id: slugify(m[2].trim()) }
        })
        .filter(Boolean)
    },
    scrollTo(id) {
      const el = this.$refs.contentPane?.querySelector(`#${CSS.escape(id)}`)
      if (!el) return
      this.activeId = id
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    setupObserver() {
      if (this.$tocObserver) this.$tocObserver.disconnect()
      if (!this.toc.length) return

      const scrollContainer = this.$refs.contentPane
      if (!scrollContainer) return
      const headings = this.toc
        .map(item => scrollContainer.querySelector(`#${CSS.escape(item.id)}`))
        .filter(Boolean)

      this.$tocObserver = new IntersectionObserver(
        entries => {
          const visible = entries.filter(e => e.isIntersecting)
          if (visible.length) {
            this.activeId = visible[0].target.id
          }
        },
        { root: scrollContainer, rootMargin: '0px 0px -70% 0px', threshold: 0 }
      )
      headings.forEach(h => this.$tocObserver.observe(h))
    },
  },
}
</script>

<style scoped>
/* ── Layout ── */
.manuel-root {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.page-layout {
  display: flex;
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
}

.toc-pane {
  flex: 0 0 25%;
  max-width: 280px;
  height: 100%;
  overflow-y: auto;
  border-right: 1px solid #e0e0e0;
}

.content-pane {
  flex: 1;
  height: 100%;
  overflow-y: auto;
}

.toc-link {
  display: block;
  text-decoration: none;
  color: #555;
  font-size: 0.82rem;
  line-height: 1.5;
  padding: 3px 0;
  transition: color 0.15s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.toc-link:hover,
.toc-link.toc-active {
  color: #1976d2;
  font-weight: 600;
}
.toc-level-1 { padding-left: 0; font-weight: 600; font-size: 0.85rem; margin-top: 6px; }
.toc-level-2 { padding-left: 12px; }
.toc-level-3 { padding-left: 24px; color: #777; font-size: 0.78rem; }

/* ── Manuel content ── */
.manuel-content {
  font-size: 15px;
  line-height: 1.7;
}

.manuel-content :deep(h1) {
  font-size: 1.8rem;
  font-weight: 700;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

.manuel-content :deep(h2) {
  font-size: 1.4rem;
  font-weight: 600;
  margin-top: 2rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 0.25rem;
}

.manuel-content :deep(h3) {
  font-size: 1.15rem;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.4rem;
}

.manuel-content :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 1rem 0;
}

.manuel-content :deep(th),
.manuel-content :deep(td) {
  border: 1px solid #ddd;
  padding: 8px 12px;
  text-align: left;
}

.manuel-content :deep(th) {
  background-color: rgba(128, 128, 128, 0.1);
  font-weight: 600;
}

.manuel-content :deep(blockquote) {
  border-left: 4px solid #ff9800;
  margin: 1rem 0;
  padding: 0.5rem 1rem;
  background: rgba(255, 193, 7, 0.1);
  border-radius: 0 4px 4px 0;
}

.manuel-content :deep(code) {
  background: rgba(128, 128, 128, 0.1);
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.9em;
}

.manuel-content :deep(pre) {
  background: rgba(128, 128, 128, 0.1);
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
}

.manuel-content :deep(hr) {
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 1.5rem 0;
}

.manuel-content :deep(figure.manuel-screenshot) {
  margin: 1rem 0;
  text-align: center;
}

.manuel-content :deep(figure.manuel-screenshot img) {
  max-width: 480px;
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.manuel-content :deep(figure.manuel-screenshot figcaption) {
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: #777;
  font-style: italic;
}
</style>
